/**
 * @fileoverview Type guard functions for resource types
 * Provides type guards to check and narrow resource types at runtime
 * 
 * These type guards follow TypeScript's discriminated union pattern to narrow types
 * based on the 'resourceType' and 'toolType' discriminants.
 * 
 * Usage examples:
 * 
 * // Basic type checking
 * if (isGuide(resource)) {
 *   // TypeScript knows resource is a Guide here
 *   console.log(resource.guideType);
 * }
 * 
 * // With type assertion
 * const resources = data.filter(isGuide);
 * // resources is now Guide[]
 * 
 * // With dynamic type checking
 * if (isResourceType(resource, ResourceType.TEMPLATE)) {
 *   // TypeScript knows resource has resourceType === 'template'
 * }
 */

import { ResourceType, ToolType } from '~/lib/resources/types/shared/enums'
import type { BaseResource, BaseTool, ResourceInstance } from '~/lib/resources/types/shared/base'
import type { Calculator } from '~/lib/resources/types/tools/calculator'
import type { Analyzer, Generator } from '~/lib/resources/types/tools'
import type { Quiz } from '~/lib/resources/types/tools/quiz'
import type { Guide } from '~/lib/resources/types/guides'
import type { Template } from '~/lib/resources/types/templates'
import type { CaseStudy } from '~/lib/resources/types/case-studies'
import type { Ebook } from '~/lib/resources/types/ebooks'
import type { 
  GlossaryTerm, 
  MetricTerm, 
  ChartTerm, 
  PlatformTerm, 
  GeneralTerm, 
  CreativeTerm 
} from '~/lib/resources/types/glossary/terms'

// =========================================
// Base Resource Type Guards
// =========================================

/**
 * Type guard to check if a value is a BaseResource
 * Validates that the value has all required BaseResource properties
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value matches BaseResource interface
 */
export function isBaseResource(value: unknown): value is BaseResource {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'title' in value &&
    'description' in value &&
    'resourceType' in value &&
    'slug' in value &&
    typeof (value as BaseResource).resourceType === 'string'
  )
}

/**
 * Type guard to check if a value is a ResourceInstance
 * Validates that the value is a BaseResource with a valid resourceType
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a valid ResourceInstance
 */
export function isResourceInstance(value: unknown): value is ResourceInstance {
  if (!isBaseResource(value)) return false
  
  const resourceType = value.resourceType
  
  // Check if resourceType is one of the valid types
  if (!Object.values(ResourceType).includes(resourceType)) {
    return false
  }
  
  // Additional validation for tools
  if (resourceType === ResourceType.TOOL) {
    return isTool(value)
  }
  
  return true
}

// =========================================
// Resource Type Guards
// =========================================

/**
 * Type guard for GlossaryTerm resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a GlossaryTerm
 */
export function isGlossaryTerm(value: unknown): value is GlossaryTerm {
  return isBaseResource(value) && value.resourceType === ResourceType.GLOSSARY_TERM
}

/**
 * Type guard for MetricTerm resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a MetricTerm
 */
export function isMetricTerm(value: unknown): value is MetricTerm {
  return isGlossaryTerm(value) && 'termType' in value && value.termType === 'metric'
}

/**
 * Type guard for ChartTerm resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a ChartTerm
 */
export function isChartTerm(value: unknown): value is ChartTerm {
  return isGlossaryTerm(value) && 'termType' in value && value.termType === 'chart'
}

/**
 * Type guard for PlatformTerm resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a PlatformTerm
 */
export function isPlatformTerm(value: unknown): value is PlatformTerm {
  return isGlossaryTerm(value) && 'termType' in value && value.termType === 'platform'
}

/**
 * Type guard for GeneralTerm resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a GeneralTerm
 */
export function isGeneralTerm(value: unknown): value is GeneralTerm {
  return isGlossaryTerm(value) && 'termType' in value && value.termType === 'general'
}

/**
 * Type guard for CreativeTerm resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a CreativeTerm
 */
export function isCreativeTerm(value: unknown): value is CreativeTerm {
  return isGlossaryTerm(value) && 'termType' in value && value.termType === 'creative'
}

/**
 * Type guard for Guide resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a Guide
 */
export function isGuide(value: unknown): value is Guide {
  return isBaseResource(value) && value.resourceType === ResourceType.GUIDE
}

/**
 * Type guard for Template resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a Template
 */
export function isTemplate(value: unknown): value is Template {
  return isBaseResource(value) && value.resourceType === ResourceType.TEMPLATE
}

/**
 * Type guard for CaseStudy resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a CaseStudy
 */
export function isCaseStudy(value: unknown): value is CaseStudy {
  return isBaseResource(value) && value.resourceType === ResourceType.CASE_STUDY
}

/**
 * Type guard for Ebook resources
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is an Ebook
 */
export function isEbook(value: unknown): value is Ebook {
  return isBaseResource(value) && value.resourceType === ResourceType.EBOOK
}

// =========================================
// Tool Type Guards
// =========================================

/**
 * Type guard to check if a resource is a tool
 * Validates base resource properties and tool-specific fields
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value matches BaseTool interface
 */
export function isTool(value: unknown): value is BaseTool {
  return (
    isBaseResource(value) &&
    value.resourceType === ResourceType.TOOL &&
    'toolType' in value &&
    typeof (value as BaseTool).toolType === 'string' &&
    isToolType((value as BaseTool).toolType)
  )
}

/**
 * Type guard to check if a value is a Calculator.
 *
 * Strict: a tool of type CALCULATOR that carries a `config` object. This is the
 * canonical home for the guard — the byte-equivalent duplicate that lived in
 * `types/tools/calculator.ts` was removed (A5/D1 dedup).
 *
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a Calculator
 */
export function isCalculator(value: unknown): value is Calculator {
  // `config != null` guards against `config: null` — `typeof null === 'object'`
  // in JS, so a bare `typeof === 'object'` would narrow to a `Calculator` whose
  // `config` is non-null in the type but null at runtime (`r3455198475`).
  return (
    isTool(value) &&
    value.toolType === ToolType.CALCULATOR &&
    (value as Calculator).config != null &&
    typeof (value as Calculator).config === 'object'
  )
}

/**
 * Type guard to check if a value is an Analyzer.
 *
 * Strict: a tool of type ANALYZER carrying a `config` object. Canonical home;
 * the duplicate in `types/tools/analyzer.ts` was removed (A5/D1).
 *
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is an Analyzer
 */
export function isAnalyzer(value: unknown): value is Analyzer {
  // See `isCalculator`: reject `config: null` (`typeof null === 'object'`).
  return (
    isTool(value) &&
    value.toolType === ToolType.ANALYZER &&
    (value as Analyzer).config != null &&
    typeof (value as Analyzer).config === 'object'
  )
}

/**
 * Type guard to check if a value is a Quiz.
 *
 * Strict: a tool of type QUIZ that carries both `sections` and `config`.
 * Canonical home; the duplicate in `types/tools/quiz.ts` and the
 * `BaseTool`-narrowed copy in `data/quizzes/index.ts` were removed (A5/D1).
 *
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a Quiz
 */
export function isQuiz(value: unknown): value is Quiz {
  // Same class of fix as `isCalculator`: `'config' in value` is true for
  // `config: null`, so require a non-null `config` object (and `sections`).
  return (
    isTool(value) &&
    value.toolType === ToolType.QUIZ &&
    'sections' in value &&
    (value as Quiz).config != null &&
    typeof (value as Quiz).config === 'object'
  )
}

/**
 * Type guard to check if a value is a Generator.
 *
 * Strict: a tool of type GENERATOR whose `config.steps` is an array. Canonical
 * home; the duplicate in `types/tools/generator.ts` was removed (A5/D1).
 *
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a Generator
 */
export function isGenerator(value: unknown): value is Generator {
  return (
    isTool(value) &&
    value.toolType === ToolType.GENERATOR &&
    (value as Generator).config != null &&
    typeof (value as Generator).config === 'object' &&
    Array.isArray((value as Generator).config?.steps)
  )
}

/**
 * Type guard to check if a string is a valid ToolType
 * 
 * @param {unknown} value - Value to check
 * @returns {boolean} True if value is a valid tool type
 */
export function isToolType(value: unknown): value is ToolType {
  return typeof value === 'string' && 
         Object.values(ToolType).includes(value as ToolType)
}

/**
 * Type guard to check if a value is a specific resource type
 * This is a type-safe implementation that ensures the value matches the specific resource type interface
 * 
 * @param {unknown} value - Value to check
 * @param {ResourceType} type - Resource type to check against
 * @returns {boolean} True if value matches the specified resource type
 */
export function isResourceType(value: unknown, type: ResourceType): value is BaseResource {
  if (!isBaseResource(value)) return false
  return value.resourceType === type
}

/**
 * Type guard to check if a value is a specific tool type
 * Combines resource type and tool type checking in one function
 * 
 * @param {unknown} value - Value to check
 * @param {ToolType} toolType - Tool type to check against
 * @returns {boolean} True if value matches the specified tool type
 */
export function isToolOfType(value: unknown, toolType: ToolType): value is BaseTool {
  return isTool(value) && (value).toolType === toolType
}

/**
 * Type-safe filter function for resource arrays
 * Filters an array to only include items of a specific resource type
 * 
 * @template T extends BaseResource
 * @param {unknown[]} items - Array of items to filter
 * @param {(item: unknown) => item is T} typeGuard - Type guard function to apply
 * @returns {T[]} Array containing only items of the specified type
 * 
 * @example
 * // Get all guides from a mixed resource array
 * const guides = filterResourcesByType(resources, isGuide);
 */
export function filterResourcesByType<T extends BaseResource>(
  items: unknown[],
  typeGuard: (item: unknown) => item is T
): T[] {
  return items.filter(typeGuard)
}

/**
 * Safely cast a resource to a specific type if it matches
 * Returns the typed resource or null if it doesn't match
 * 
 * @template T extends BaseResource
 * @param {unknown} value - Resource to cast
 * @param {(value: unknown) => value is T} typeGuard - Type guard function to apply
 * @returns {T | null} The resource cast to type T or null if not matching
 * 
 * @example
 * // Safely get a guide or null
 * const guide = castResourceOrNull(resource, isGuide);
 * if (guide) {
 *   // Use guide with proper typing
 * }
 */
export function castResourceOrNull<T extends BaseResource>(
  value: unknown,
  typeGuard: (value: unknown) => value is T
): T | null {
  return typeGuard(value) ? value : null
}